'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  RotateCw,
  Trash2,
} from 'lucide-react';

import { PdfViewer, VideoPlayer } from '@kit/documents/components';
import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@kit/ui/dialog';
import { ImageCanvas } from '@kit/ui/image-viewer';
import { cn } from '@kit/ui/utils';

import type { MediaFile } from '../hooks/use-media-upload';
import { resolveVideoSource } from '../lib/video-source';
import { inferCategory, isPdf, thumbnailSrc } from './media-thumb';
import { MuxHlsPlayer } from './mux-hls-player';

export interface MediaLightboxTranslations {
  close?: string;
  previous?: string;
  next?: string;
  replace?: string;
  delete?: string;
  replacing?: string;
  fileLabel?: string;
}

const DEFAULTS: Required<MediaLightboxTranslations> = {
  close: 'Close',
  previous: 'Previous',
  next: 'Next',
  replace: 'Replace',
  delete: 'Delete',
  replacing: 'Replacing…',
  fileLabel: 'Media preview',
};

interface MediaLightboxProps {
  files: MediaFile[];
  /** Index of the file currently displayed; null = closed */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  onDelete?: (file: MediaFile) => void;
  /** Provide an onReplace handler to enable the Replace button. The handler
   * receives the new browser File and the original file id. */
  onReplace?: (originalId: string, newFile: File) => void;
  /** Accepted MIME types for the file picker (Replace flow). */
  replaceAccept?: string;
  isReplacing?: boolean;
  isDeleting?: boolean;
  translations?: MediaLightboxTranslations;
}

/**
 * Fullscreen lightbox for previewing uploaded media.
 *
 * - Image: fits the viewport with `object-contain`.
 * - Video: uses `<VideoPlayer>` with full player controls.
 * - PDF: renders the document in a full-height `<iframe>` (native viewer).
 * - Other documents: fall back to the filename label.
 *
 * Footer actions: Replace (re-uses the OS file picker) and Delete.
 * Keyboard: Esc closes; ←/→ navigate when there's more than one file.
 */
export function MediaLightbox({
  files,
  index,
  onIndexChange,
  onClose,
  onDelete,
  onReplace,
  replaceAccept,
  isReplacing,
  isDeleting,
  translations: customTranslations,
}: MediaLightboxProps) {
  const t = { ...DEFAULTS, ...customTranslations };
  const open = index !== null;
  const file = open ? files[index] : null;
  const replaceInputRef = useRef<HTMLInputElement>(null);

  const goPrev = useCallback(() => {
    if (index === null) return;
    onIndexChange((index - 1 + files.length) % files.length);
  }, [index, files.length, onIndexChange]);

  const goNext = useCallback(() => {
    if (index === null) return;
    onIndexChange((index + 1) % files.length);
  }, [index, files.length, onIndexChange]);

  // Keyboard navigation when open and multi-item
  useEffect(() => {
    if (!open || files.length <= 1) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, files.length, goPrev, goNext]);

  if (!file) return null;

  const category = inferCategory(file);
  const src = thumbnailSrc(file);

  // Route video playback through the shared resolver (BAL-721): a migrated
  // video plays from Mux HLS / R2 CDN instead of the dead Supabase-Storage URL.
  const videoSource =
    category === 'video'
      ? resolveVideoSource({
          externalUrl: file.externalUrl,
          uploadedUrl: src,
          muxPlaybackId: file.muxPlaybackId,
          muxStatus: file.muxStatus,
          r2Key: file.r2Key,
        })
      : null;
  const nativeVideoUrl =
    videoSource?.kind === 'r2' || videoSource?.kind === 'native'
      ? videoSource.url
      : src;

  const handleReplaceClick = () => replaceInputRef.current?.click();
  const handleReplaceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    if (!picked || !onReplace) return;
    onReplace(file.id, picked);
    // Reset so picking the same filename twice still triggers change.
    event.target.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-background/98 flex h-[92dvh] max-w-[min(96vw,1280px)] flex-col gap-0 p-0 sm:rounded-lg">
        <DialogTitle className="sr-only">
          {file.fileName ?? t.fileLabel}
        </DialogTitle>
        <DialogDescription className="sr-only">{t.fileLabel}</DialogDescription>

        {/* Asset stage */}
        <div className="relative flex flex-1 items-center justify-center overflow-hidden p-2 sm:p-6">
          {category === 'image' && src ? (
            <ImageCanvas
              src={src}
              alt={file.fileName ?? ''}
              zoom={100}
              className="p-0"
            />
          ) : category === 'video' && videoSource?.kind === 'mux' && file.id ? (
            <div className="flex max-h-full max-w-full items-center justify-center">
              <MuxHlsPlayer
                mediaId={file.id}
                controls
                autoPlay
                aria-label={file.fileName ?? t.fileLabel}
                className="max-h-[80dvh] w-full max-w-[1100px] rounded bg-black"
              />
            </div>
          ) : category === 'video' && nativeVideoUrl ? (
            <div className="flex max-h-full max-w-full items-center justify-center">
              <VideoPlayer
                src={nativeVideoUrl}
                controls
                className="max-h-[80dvh] w-full max-w-[1100px] rounded"
              />
            </div>
          ) : category === 'document' && src && isPdf(file) ? (
            <div className="h-full w-full overflow-hidden rounded">
              <PdfViewer url={src} fileName={file.fileName ?? t.fileLabel} />
            </div>
          ) : (
            <div className="text-muted-foreground p-8 text-center text-sm">
              {file.fileName ?? t.fileLabel}
            </div>
          )}

          {/* Prev / Next */}
          {files.length > 1 && (
            <>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label={t.previous}
                onClick={goPrev}
                className="bg-background/70 absolute top-1/2 left-3 size-10 -translate-y-1/2 rounded-full backdrop-blur-sm"
              >
                <ChevronLeft className="size-5" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label={t.next}
                onClick={goNext}
                className="bg-background/70 absolute top-1/2 right-3 size-10 -translate-y-1/2 rounded-full backdrop-blur-sm"
              >
                <ChevronRight className="size-5" />
              </Button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-foreground/10 flex items-center justify-between gap-3 border-t px-4 py-3 sm:px-6">
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                'truncate text-sm font-medium',
                !file.fileName && 'text-muted-foreground italic',
              )}
              title={file.fileName ?? undefined}
            >
              {file.fileName ?? t.fileLabel}
            </p>
            {files.length > 1 && index !== null && (
              <p className="text-muted-foreground/80 mt-0.5 font-mono text-[10px] tracking-wider tabular-nums">
                {index + 1} / {files.length}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {onReplace && (
              <>
                {/* eslint-disable-next-line kit-components/use-kit-primitives -- hidden file input triggered programmatically */}
                <input
                  ref={replaceInputRef}
                  type="file"
                  accept={replaceAccept}
                  className="sr-only"
                  onChange={handleReplaceChange}
                  aria-hidden="true"
                  tabIndex={-1}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleReplaceClick}
                  disabled={isReplacing || isDeleting}
                  className="h-9"
                >
                  {isReplacing ? (
                    <>
                      <Loader2 className="mr-2 size-3.5 animate-spin" />
                      {t.replacing}
                    </>
                  ) : (
                    <>
                      <RotateCw className="mr-2 size-3.5" />
                      {t.replace}
                    </>
                  )}
                </Button>
              </>
            )}
            {onDelete && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => onDelete(file)}
                disabled={isDeleting || isReplacing}
                className="h-9"
              >
                {isDeleting ? (
                  <Loader2 className="mr-2 size-3.5 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 size-3.5" />
                )}
                {t.delete}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
